import React from 'react';
import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonImg,
  IonItem,
  IonLabel,
  IonList,
  IonText,
} from '@ionic/react';
import { PublicReportResponse, SupportedPublicLocale } from '../types';
import { formatCompletionDate } from '../dateUtils';
import appLogoLight from '../../../assets/images/app-logo-light.png';
import appLogoDark from '../../../assets/images/app-logo-dark.png';

interface PublicCleaningReportProps {
  report: PublicReportResponse;
  locale: SupportedPublicLocale;
}

const translations = {
  en: {
    pageTitle: 'Public Report Record For',
    recentDates: 'Recent Dates of Service',
    taskDetails: 'Task Completion Details',
    timezoneNote: 'Times shown in local device time.',
    noCompletions: 'No completions recorded',
    downloadApp: 'Download the App',
    detailsNote: 'For details beyond the most recent two service dates, please reach out to your service provider.',
  },
  fr: {
    pageTitle: 'Rapport public pour',
    recentDates: 'Dates de service récentes',
    taskDetails: "Détails d'achèvement des tâches",
    timezoneNote: 'Heures affichées selon le fuseau horaire de votre appareil.',
    noCompletions: 'Aucune complétion enregistrée',
    downloadApp: "Télécharger l'application",
    detailsNote:
      'Pour plus de détails au-delà des deux dates de service les plus récentes, veuillez contacter votre prestataire de services.',
  },
};

const APP_STORE_URL = 'https://apps.apple.com/app/otuvy-frontline/id6461824661';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.otuvy.frontline';

const PublicCleaningReport: React.FC<PublicCleaningReportProps> = ({ report, locale }) => {
  const t = translations[locale];
  const isDarkMode = document.body.classList.contains('dark');

  return (
    <div className="ion-padding">
      {/* Logo */}
      <div className="ion-text-center ion-padding-vertical">
        <IonImg
          src={isDarkMode ? appLogoDark : appLogoLight}
          alt="Otuvy Frontline"
          className="margin-auto"
          style={{ height: '48px', width: 'auto' }}
        />
      </div>

      {/* Title */}
      <IonText color="primary">
        <h1 className="ion-text-center font-24 font-weight-600 no-margin-top small-margin-bottom">{t.pageTitle}</h1>
      </IonText>
      <p className="ion-text-center font-18 no-vertical-margin">{report.listName}</p>
      <IonText color="medium">
        <p className="ion-text-center font-12 ion-margin-top">{t.detailsNote}</p>
      </IonText>

      {/* Recent Dates of Service */}
      <IonCard className="ion-margin-top">
        <IonCardHeader>
          <IonCardTitle color="primary" className="font-18 font-weight-600">
            {t.recentDates}
          </IonCardTitle>
        </IonCardHeader>
        <IonCardContent className="no-padding-top">
          {report.recentDatesOfService.length > 0 ? (
            <IonList lines="inset" className="ion-no-padding">
              {report.recentDatesOfService.map((date, index) => (
                <IonItem key={index} className="min-height-auto ion-no-padding">
                  <IonLabel className="font-16">{formatCompletionDate(date, locale)}</IonLabel>
                </IonItem>
              ))}
            </IonList>
          ) : (
            <IonText color="medium">
              <p className="ion-no-margin font-14 font-italic">{t.noCompletions}</p>
            </IonText>
          )}
        </IonCardContent>
      </IonCard>

      {/* Task Completion Details */}
      <IonCard>
        <IonCardHeader>
          <IonCardTitle color="primary" className="font-18 font-weight-600">
            {t.taskDetails}
          </IonCardTitle>
        </IonCardHeader>
        <IonCardContent className="no-padding-top">
          <IonList lines="inset" className="ion-no-padding">
            {report.tasks.map((task) => (
              <IonItem key={task.taskId} className="min-height-auto ion-no-padding">
                <IonLabel>
                  <h3 className="font-16 font-weight-500">{task.taskName}</h3>
                  <IonText color="medium">
                    {task.lastTwoCompletions.length > 0 ? (
                      task.lastTwoCompletions.map((date, index) => (
                        <p key={index} className="font-14">
                          {formatCompletionDate(date, locale)}
                        </p>
                      ))
                    ) : (
                      <p className="font-14 font-italic">{t.noCompletions}</p>
                    )}
                  </IonText>
                </IonLabel>
              </IonItem>
            ))}
          </IonList>
        </IonCardContent>
      </IonCard>

      {/* Timezone Note */}
      <IonText color="medium">
        <p className="ion-text-center font-12 ion-margin-vertical">{t.timezoneNote}</p>
      </IonText>

      {/* App Store Badges */}
      <div className="ion-text-center ion-padding-vertical">
        <p className="font-16 font-weight-500 small-margin-bottom">{t.downloadApp}</p>
        <div className="d-flex flex-direction-column align-center gap-sm">
          <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
            <img
              src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
              alt="Download on the App Store"
              style={{ height: '44px' }}
            />
          </a>
          <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
            <img
              src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
              alt="Get it on Google Play"
              style={{ height: '64px' }}
            />
          </a>
        </div>
      </div>
    </div>
  );
};

export default PublicCleaningReport;
